import { useEffect, useState } from 'react';
import ServerList from "./ServerList.jsx";
import ChannelSidebar from "./ChannelSidebar.jsx";
import ChatArea from "./ChatArea.jsx";
import MemberSidebar from "./MemberSidebar.jsx";
import DirectMessageList from "./DirectMessageList.jsx";
import DirectMessageChat from "./DirectMessageChat.jsx";
import { ModalProvider } from "./modals/ModalProvider.jsx";
import KeyboardShortcuts from "./KeyboardShortcuts.jsx";
import { ToastProvider } from "./ToastProvider.jsx";
import ActivityPanel from "./ActivityPanel.jsx";
import SettingsPanel from "./SettingsPanel.jsx";
import VoiceHUD from "./VoiceHUD.jsx";
import IncomingCallToast from "./IncomingCallToast.jsx";
import { useDirectMessageStore } from "../../stores/directMessageStore.js";
import { useServerStore } from "../../stores/serverStore.js";
import { useChannelStore } from "../../stores/channelStore.js";
import { useMessageStore } from "../../stores/messageStore.js";
import { useUserSettingsStore } from "../../stores/userSettingsStore.js";
import { useAeThex } from "../../components/AeThexProvider";
import { useNotifications } from "../../hooks/useNotifications.js";

function KeyboardHints() {
  const [show, setShow] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setShow(false), 5000);
    return () => clearTimeout(t);
  }, []);
  if (!show) return null;
  return (
    <div style={{position:'fixed',bottom:16,left:'50%',transform:'translateX(-50%)',display:'flex',alignItems:'center',gap:16,padding:'8px 16px',background:'rgba(0,0,0,0.8)',backdropFilter:'blur(12px)',borderRadius:9999,border:'1px solid rgba(255,255,255,0.1)',fontSize:12,color:'rgba(156,163,175,1)',zIndex:1000}}>
      <span><kbd style={{padding:'2px 6px',background:'rgba(255,255,255,0.1)',borderRadius:4}}>Ctrl</kbd> + <kbd style={{padding:'2px 6px',background:'rgba(255,255,255,0.1)',borderRadius:4}}>,</kbd> Settings</span>
      <span style={{width:1,height:16,background:'rgba(255,255,255,0.1)'}} />
      <span><kbd style={{padding:'2px 6px',background:'rgba(255,255,255,0.1)',borderRadius:4}}>Ctrl</kbd> + <kbd style={{padding:'2px 6px',background:'rgba(255,255,255,0.1)',borderRadius:4}}>K</kbd> Quick Switcher</span>
      <button onClick={() => setShow(false)} style={{marginLeft:8,color:'rgba(107,114,128,1)',background:'none',border:'none',cursor:'pointer'}}>✕</button>
    </div>
  );
}

export default function MainLayout() {
  const currentConversationId = useDirectMessageStore((state) => state.currentConversationId);

  // Store actions
  const fetchServers = useServerStore((state) => state.fetchServers);
  const currentServerId = useServerStore((state) => state.currentServerId);
  const fetchChannels = useChannelStore((state) => state.fetchChannels);
  const fetchMessages = useMessageStore((state) => state.fetchMessages);
  const currentChannelId = useChannelStore((state) => state.currentChannelId);
  const initializeUser = useUserSettingsStore((state) => state.initializeUser);
  const initializeSocketListeners = useMessageStore((state) => state.initializeSocketListeners);
  const { user } = useAeThex();

  const [showActivity,      setShowActivity]      = useState(false);
  const [showSettings,      setShowSettings]      = useState(false);
  const [mobileChannelOpen, setMobileChannelOpen] = useState(false);
  const [mobileMemberOpen,  setMobileMemberOpen]  = useState(false);

  useNotifications();

  // Initialize user on mount (always)
  useEffect(() => {
    initializeUser();
  }, []);

  // Load servers and socket listeners only after user is authenticated
  useEffect(() => {
    if (user) {
      fetchServers();
      initializeSocketListeners();
    }
  }, [user]);

  // Load channels when server changes
  useEffect(() => {
    if (currentServerId) {
      fetchChannels(currentServerId);
    }
  }, [currentServerId]);

  // Load messages when channel changes
  useEffect(() => {
    if (currentChannelId && !currentConversationId) {
      fetchMessages(currentChannelId);
    }
  }, [currentChannelId, currentConversationId]);

  // Keyboard shortcut: Ctrl+, opens settings
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === ',') {
        e.preventDefault();
        setShowSettings((v) => !v);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <ToastProvider>
      <a href="#chat-messages" className="skip-to-content">Skip to messages</a>
      <KeyboardShortcuts />
      <div className="connect-container flex h-screen">
        {/* Ambient glow effects */}
        <div style={{position:'fixed',top:0,left:'25%',width:384,height:384,background:'rgba(99,102,241,0.06)',borderRadius:'50%',filter:'blur(120px)',pointerEvents:'none'}} />
        <div style={{position:'fixed',bottom:0,right:'25%',width:384,height:384,background:'rgba(147,51,234,0.06)',borderRadius:'50%',filter:'blur(120px)',pointerEvents:'none'}} />
        <div style={{position:'fixed',top:'50%',left:'50%',transform:'translate(-50%,-50%)',width:256,height:256,background:'rgba(236,72,153,0.04)',borderRadius:'50%',filter:'blur(100px)',pointerEvents:'none'}} />

        <ServerList />

        {/* Mobile overlay backdrop */}
        {(mobileChannelOpen || mobileMemberOpen) && (
          <div className="mobile-overlay-backdrop"
            onClick={() => { setMobileChannelOpen(false); setMobileMemberOpen(false); }}
          />
        )}

        {currentConversationId ? (
          <>
            <DirectMessageList />
            <DirectMessageChat />
          </>
        ) : (
          <>
            <ChannelSidebar
              onOpenSettings={() => setShowSettings(true)}
              mobileOpen={mobileChannelOpen}
              onMobileClose={() => setMobileChannelOpen(false)}
            />
            <ChatArea
              onOpenSettings={() => setShowSettings(true)}
              onToggleActivity={() => setShowActivity((v) => !v)}
              onMobileOpenChannels={() => setMobileChannelOpen(true)}
              onMobileOpenMembers={() => setMobileMemberOpen(true)}
            />
            <MemberSidebar
              onToggleActivity={() => setShowActivity((v) => !v)}
              mobileOpen={mobileMemberOpen}
              onMobileClose={() => setMobileMemberOpen(false)}
            />
            {showActivity && <ActivityPanel />}
          </>
        )}
      </div>
      <VoiceHUD />
      <IncomingCallToast />
      <ModalProvider />
      <KeyboardHints />
      <SettingsPanel
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        onToggleActivity={() => setShowActivity((v) => !v)}
      />
    </ToastProvider>
  );
}
